import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import {
  SwapLegsContainer, SwapLegPanel, SwapLegHeader, SwapLegTitle, SwapLegIcon,
  SwapArrowDivider, SwapArrow, LegFormRow, LegFormRowSingle
} from './shared/SwapLegStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars, SettlementQuery } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import axios from 'axios';

import { DateField } from '../shared/DateField';

/**
 * Trade date calendars, as the server checks them: the swap is booked in its base (USD-side)
 * leg's currency, on that currency's calendar and USD's. Settlement also needs the counter leg's.
 */
const ccsTradeCalendars = (pay?: string | null, recv?: string | null) =>
  calendarsFor('CCS', (pay || 'USD') === 'USD' ? 'USD' : (recv || 'USD'));

// ADR 0009 §4.2: a cross-currency swap settles on the calendars of both its currencies (and USD),
// not the book's. The ticket shows the settle date the server will store; the browser's count
// stands in until the server answers.
const settleQuery = (tradeDate: string, payCurrency?: string, recvCurrency?: string): SettlementQuery =>
  ({ tradeDate, ticketType: 'CCS', convention: 'T+1', currency: payCurrency || 'USD', counterCurrency: recvCurrency || null });

/** Applies the server's settle date if the form still holds the inputs and the date it was asked about. */
const applyServerSettleDate = (setFormData: any, query: SettlementQuery, expected: string) => {
  resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === query.tradeDate && (prev.payCurrency || 'USD') === query.currency
      && (prev.recvCurrency || null) === query.counterCurrency
      && prev.settleDate === expected && settleDate && settleDate !== expected
      ? { ...prev, settleDate } : prev));
};

/** After a trade date or currency change: the browser's count at once, then the server's. */
const refreshSettleDate = (setFormData: any, query: SettlementQuery) => {
  if (!query.tradeDate) return;
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  applyServerSettleDate(setFormData, query, provisional);
};

const DerivativesCurrencySwapTicketScreen: React.FC = () => {
  const location = useLocation();

  const getPendingTradeData = () => {
    try {
      const pendingData = sessionStorage.getItem('pendingTradeData');
      if (pendingData) {
        const parsed = JSON.parse(pendingData);
        sessionStorage.removeItem('pendingTradeData');
        return parsed;
      }
    } catch (error) {
      console.error('Error reading pending trade data:', error);
    }
    return null;
  };

  const pendingTrade = getPendingTradeData();

  const getInitialFormData = () => {
    const hedgeData = location.state?.hedgeData;
    const portfolioId = location.state?.portfolioId;
    const payCurrency = pendingTrade?.payCurrency || 'USD';
    const recvCurrency = pendingTrade?.recvCurrency || 'EUR';
    const tradeDate = pendingTrade?.tradeDate || getTodayDate(ccsTradeCalendars(payCurrency, recvCurrency));

    const baseFormData = {
      // Direction: which leg the client pays
      direction: (pendingTrade?.direction || 'PAY_USD') as string,
      assetManager: pendingTrade?.assetManager || '',
      portfolioManager: pendingTrade?.portfolioManager || '',
      assetClass: 'Derivatives',
      assetSubClass: 'Currency Swaps',
      security: pendingTrade?.securityName || pendingTrade?.security || '',
      identifier: pendingTrade?.cusip || pendingTrade?.isin || pendingTrade?.identifier || '',
      // Common
      tradeDate,
      settleDate: pendingTrade?.settleDate || provisionalSettleDate(settleQuery(tradeDate, payCurrency, recvCurrency)),
      effectiveDate: pendingTrade?.effectiveDate || '',
      maturityDate: pendingTrade?.maturityDate || '',
      tenor: pendingTrade?.tenor || '',
      initialExchangeRate: pendingTrade?.initialExchangeRate?.toString() || '',
      portfolio: pendingTrade?.portfolioName || pendingTrade?.portfolio || '',
      submittedBy: pendingTrade?.submittedBy || getCurrentOperatorName(),
      executedBy: pendingTrade?.executedBy || '',
      notes: pendingTrade?.notes || '',
      // Pay Leg
      payCurrency,
      payNotional: pendingTrade?.payNotional?.toString() || '',
      payRateType: pendingTrade?.payRateType || 'FIXED',
      payFixedRate: pendingTrade?.payFixedRate?.toString() || '',
      payFloatIndex: pendingTrade?.payFloatIndex || 'SOFR',
      payFloatSpread: pendingTrade?.payFloatSpread?.toString() || '',
      payDayCount: pendingTrade?.payDayCount || '30/360',
      payFrequency: pendingTrade?.payFrequency || 'Semi-Annual',
      // Receive Leg
      recvCurrency,
      recvNotional: pendingTrade?.recvNotional?.toString() || '',
      recvRateType: pendingTrade?.recvRateType || 'FIXED',
      recvFixedRate: pendingTrade?.recvFixedRate?.toString() || '',
      recvFloatIndex: pendingTrade?.recvFloatIndex || 'EURIBOR',
      recvFloatSpread: pendingTrade?.recvFloatSpread?.toString() || '',
      recvDayCount: pendingTrade?.recvDayCount || '30/360',
      recvFrequency: pendingTrade?.recvFrequency || 'Semi-Annual',
    };

    if (hedgeData) {
      if (hedgeData.securityName) baseFormData.security = hedgeData.securityName;
      if (hedgeData.identifier) baseFormData.identifier = hedgeData.identifier;
      if (hedgeData.notionalAmount) {
        const notional = typeof hedgeData.notionalAmount === 'number'
          ? hedgeData.notionalAmount : parseFloat(hedgeData.notionalAmount);
        if (!isNaN(notional)) {
          baseFormData.payNotional = notional.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
      }
      if (portfolioId) baseFormData.portfolio = String(portfolioId);
    }

    return baseFormData;
  };

  const initialFormData = getInitialFormData();

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    if (!formData.assetManager?.trim()) errors.assetManager = 'Asset Manager is required';
    if (!formData.portfolioManager?.trim()) errors.portfolioManager = 'Portfolio Manager is required';
    if (!formData.payNotional?.trim()) errors.payNotional = 'Pay Notional is required';
    if (!formData.recvNotional?.trim()) errors.recvNotional = 'Receive Notional is required';
    if (formData.payCurrency === formData.recvCurrency) errors.recvCurrency = 'Currencies must be different';
    if (formData.payRateType === 'FIXED' && !formData.payFixedRate?.trim()) errors.payFixedRate = 'Pay Fixed Rate is required';
    if (formData.recvRateType === 'FIXED' && !formData.recvFixedRate?.trim()) errors.recvFixedRate = 'Receive Fixed Rate is required';
    if (formData.payRateType === 'FLOAT' && !formData.payFloatIndex?.trim()) errors.payFloatIndex = 'Pay Float Index is required';
    if (formData.recvRateType === 'FLOAT' && !formData.recvFloatIndex?.trim()) errors.recvFloatIndex = 'Receive Float Index is required';
    if (!formData.initialExchangeRate?.trim()) errors.initialExchangeRate = 'Initial FX Rate is required (base per 1 counter unit)';
    if (formData.payCurrency !== 'USD' && formData.recvCurrency !== 'USD') errors.payCurrency = 'One leg must be USD (portfolio base) — multi-base CCS deferred';
    if (!formData.tenor?.trim() && !formData.maturityDate) errors.tenor = 'Tenor or Maturity Date is required';
    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settle Date is required';
    if (action === 'execute') {
      if (!formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
    }
    return errors;
  };

  // CCS E2E rewire (2026-08-06, IRS/CDS/TRS template): the ticket's terms ARE
  // the bespoke contract — /prepare mints the XCCY dim_irs_contract + a
  // classified per-contract SMF (CS-prefix 9-char CUSIP) and returns the
  // sourceRef the posting SPI resolves at POST. Net-settled economics
  // (F-CCS-008 disclosed): netAmount = 0.
  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);

    // Base leg = the USD side (portfolio base); counter = the other leg.
    const payIsBase = formData.payCurrency === 'USD';
    const baseLeg = payIsBase
      ? { ccy: formData.payCurrency, notional: formData.payNotional, rate: formData.payFixedRate, freq: formData.payFrequency }
      : { ccy: formData.recvCurrency, notional: formData.recvNotional, rate: formData.recvFixedRate, freq: formData.recvFrequency };
    const counterLeg = payIsBase
      ? { ccy: formData.recvCurrency, notional: formData.recvNotional, rate: formData.recvFixedRate }
      : { ccy: formData.payCurrency, notional: formData.payNotional, rate: formData.payFixedRate };
    const baseNotional = parseAmountShorthand(baseLeg.notional);

    const prepareRes = await axios.post('/api/derivatives/ccs/prepare', {
      portfolioId: portfolio.id,
      direction: payIsBase ? 'PAY_BASE' : 'RECEIVE_BASE',
      baseCurrency: baseLeg.ccy,
      baseNotional,
      baseRate: baseLeg.rate ? parseFloat(baseLeg.rate) : null,
      counterCurrency: counterLeg.ccy,
      counterNotional: parseAmountShorthand(counterLeg.notional),
      counterRate: counterLeg.rate ? parseFloat(counterLeg.rate) : null,
      initialFxRate: formData.initialExchangeRate ? parseFloat(formData.initialExchangeRate) : null,
      paymentFrequency: baseLeg.freq || null,
      tradeDate: formData.tradeDate,
      effectiveDate: formData.effectiveDate || formData.settleDate || null,
      maturityDate: formData.maturityDate || null,
      tenor: formData.tenor || null,
    });
    const { securityId, sourceRef } = prepareRes.data;

    return {
      transactionType: payIsBase ? 'SELL' : 'BUY',
      securityId,
      portfolioId: portfolio.id,
      quantity: baseNotional,
      price: 100,
      amount: baseNotional,
      netAmount: 0,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes || `CCS ${payIsBase ? 'Pay' : 'Receive'} USD vs ${counterLeg.ccy} @ FX ${formData.initialExchangeRate}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'CCS',
      eventType: 'ACQUIRE',
      sourceRef,
    };
  };

  // Reopen/edit: contract terms live on dim_irs_contract (XCCY, resolved by
  // sourceRef), not as flat trade fields.
  const loadTransactionData = async (transaction: any) => {
    const tradeDate = transaction.tradeDate || getTodayDate(calendarsFor('CCS', transaction.currency));
    const base = {
      direction: 'PAY_USD',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: 'Derivatives',
      assetSubClass: 'Currency Swaps',
      security: transaction.securityName || transaction.security || '',
      identifier: '',
      tradeDate,
      settleDate: transaction.settleDate || provisionalSettleDate(settleQuery(tradeDate, transaction.currency)),
      effectiveDate: '',
      maturityDate: '',
      tenor: '',
      initialExchangeRate: '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      payCurrency: 'USD',
      payNotional: transaction.amount?.toString() || '',
      payRateType: 'FIXED',
      payFixedRate: '',
      payFloatIndex: 'SOFR',
      payFloatSpread: '',
      payDayCount: '30/360',
      payFrequency: 'Semi-Annual',
      recvCurrency: 'EUR',
      recvNotional: '',
      recvRateType: 'FIXED',
      recvFixedRate: '',
      recvFloatIndex: 'EURIBOR',
      recvFloatSpread: '',
      recvDayCount: '30/360',
      recvFrequency: 'Semi-Annual',
    };
    const m = (transaction.sourceRef || '').match(/ccs-contract:(\d+)/);
    if (!m) return base;
    try {
      const c = (await axios.get(`/api/derivatives/ccs/contract/${m[1]}`)).data;
      const receiveBase = c.fixedPayerReceiver !== 'PAYER';
      const baseSide = {
        ccy: c.notionalCurrency || 'USD',
        notional: c.notionalAmount != null ? String(c.notionalAmount) : '',
        rate: c.fixedRate != null ? String(parseFloat(c.fixedRate)) : '',
      };
      const counterSide = {
        ccy: c.xccyCounterCurrency || 'EUR',
        notional: c.xccyCounterNotional != null ? String(c.xccyCounterNotional) : '',
        rate: c.xccyCounterRate != null ? String(parseFloat(c.xccyCounterRate)) : '',
      };
      // receiveBase → base leg renders as the RECEIVE panel.
      const recv = receiveBase ? baseSide : counterSide;
      const pay = receiveBase ? counterSide : baseSide;
      return {
        ...base,
        direction: receiveBase ? 'RECEIVE_USD' : 'PAY_USD',
        effectiveDate: c.effectiveDate || '',
        maturityDate: c.maturityDate || '',
        initialExchangeRate: c.xccyInitialFxRate != null ? String(parseFloat(c.xccyInitialFxRate)) : '',
        payCurrency: pay.ccy, payNotional: pay.notional, payFixedRate: pay.rate,
        recvCurrency: recv.ccy, recvNotional: recv.notional, recvFixedRate: recv.rate,
        payFrequency: c.fixedPaymentFreq || base.payFrequency,
        recvFrequency: c.fixedPaymentFreq || base.recvFrequency,
      };
    } catch { return base; }
  };

  return (
    <BaseTicketScreen
      title="Cross-Currency Swap Trade Entry"
      assetClass="Derivatives"
      assetSubClass="Currency Swaps"
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, editingTransactionId }) => (
        <CurrencySwapFormContent
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          dimensionData={dimensionData}
          transactionStatus={transactionStatus}
          isFieldDisabled={isFieldDisabled}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          location={location}
          editingTransactionId={editingTransactionId}
        />
      )}
    </BaseTicketScreen>
  );
};

const CurrencySwapFormContent: React.FC<{
  formData: any;
  setFormData: any;
  errors: any;
  dimensionData: any;
  transactionStatus: any;
  isFieldDisabled: any;
  handleInputChange: any;
  handleInputBlur: any;
  location: any;
  editingTransactionId: number | null;
}> = ({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, location, editingTransactionId }) => {

  useEffect(() => {
    const portfolioId = location.state?.portfolioId;
    const portfolioName = location.state?.portfolioName;
    const currentPortfolio = formData.portfolio;
    const isPortfolioId = currentPortfolio && /^\d+$/.test(String(currentPortfolio).trim());
    if (portfolioName && (!currentPortfolio || isPortfolioId)) {
      setFormData((prev: any) => ({ ...prev, portfolio: portfolioName }));
    } else if (portfolioId && dimensionData.portfolios.length > 0 && (!currentPortfolio || isPortfolioId)) {
      const portfolio = dimensionData.portfolios.find((p: any) => String(p.id) === String(portfolioId));
      if (portfolio?.portfolioName) {
        setFormData((prev: any) => ({ ...prev, portfolio: portfolio.portfolioName }));
      }
    }
  }, [dimensionData.portfolios, location.state?.portfolioId, location.state?.portfolioName, formData.portfolio, setFormData]);

  // A new ticket asks the server for its settle date on open; a reopened trade keeps its own.
  useEffect(() => {
    if (editingTransactionId || location.state?.transactionId || location.state?.id || !formData.tradeDate) return;
    applyServerSettleDate(setFormData, settleQuery(formData.tradeDate, formData.payCurrency, formData.recvCurrency),
      formData.settleDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* ── Header Row: Trade Date, Settle Date, Exchange Rate ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="tradeDate">Trade Date*</Label>
          <DateField
            id="tradeDate"
            value={formData.tradeDate ?? ''}
            className={errors.tradeDate ? 'error' : ''}
            disabled={isFieldDisabled('tradeDate')}
            calendars={ccsTradeCalendars(formData.payCurrency, formData.recvCurrency)}
            onChange={iso => {
              handleInputChange('tradeDate', iso);
              refreshSettleDate(setFormData, settleQuery(iso, formData.payCurrency, formData.recvCurrency));
            }}
          />
          {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="settleDate">Settle Date*</Label>
          <DateField
            id="settleDate"
            value={formData.settleDate ?? ''}
            className={errors.settleDate ? 'error' : ''}
            disabled={isFieldDisabled('settleDate')}
            calendars={settlementCalendars({
              ticketType: 'CCS', currency: formData.payCurrency, counterCurrency: formData.recvCurrency,
            })}
            onChange={iso => handleInputChange('settleDate', iso)}
          />
          {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="initialExchangeRate">Initial FX Rate</Label>
          <Input
            id="initialExchangeRate"
            type="number"
            step="0.0001"
            value={formData.initialExchangeRate}
            onChange={(e) => handleInputChange('initialExchangeRate', e.target.value)}
            placeholder="e.g., 1.0850"
            disabled={isFieldDisabled('initialExchangeRate')}
          />
        </FormGroup>
      </FormRow>

      {/* ── Row 2: Asset Manager, PM, Executed By ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="assetManager">Asset Manager*</Label>
          <Select
            id="assetManager"
            value={formData.assetManager}
            onChange={(e) => handleInputChange('assetManager', e.target.value)}
            className={errors.assetManager ? 'error' : ''}
            disabled={isFieldDisabled('assetManager')}
          >
            <option value="">Select Asset Manager</option>
            {dimensionData.assetManagers.map((am: any) => (
              <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
            ))}
          </Select>
          {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="portfolioManager">Portfolio Manager*</Label>
          <Select
            id="portfolioManager"
            value={formData.portfolioManager}
            onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
            className={errors.portfolioManager ? 'error' : ''}
            disabled={isFieldDisabled('portfolioManager')}
          >
            <option value="">Select Portfolio Manager</option>
            {dimensionData.portfolioManagers.map((pm: any) => (
              <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
            ))}
          </Select>
          {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
          <Select
            id="executedBy"
            value={formData.executedBy}
            onChange={(e) => handleInputChange('executedBy', e.target.value)}
            disabled={isFieldDisabled('executedBy')}
            className={errors.executedBy ? 'error' : ''}
          >
            <option value="">Select</option>
            {dimensionData.users.map((user: any) => (
              <option key={user.id} value={user.fullName}>{user.fullName} {user.role ? `(${user.role})` : ''}</option>
            ))}
          </Select>
          {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 3: Contract Name, Identifier, Tenor ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="security">Contract Name</Label>
          <Input
            id="security"
            type="text"
            value={formData.security}
            onChange={(e) => handleInputChange('security', e.target.value)}
            placeholder="e.g., USD/EUR 5Y XCCY"
            disabled={isFieldDisabled('security')}
          />
        </FormGroup>

        {/* Identifier field REMOVED (CCS E2E 2026-08-06): the contract +
            classified SMF mint from the ticket's terms at /prepare. */}
        <FormGroup>
          <Label htmlFor="tenor">Tenor*</Label>
          <Input
            id="tenor"
            type="text"
            value={formData.tenor}
            onChange={(e) => handleInputChange('tenor', e.target.value)}
            placeholder="e.g., 5Y, 10Y"
            className={errors.tenor ? 'error' : ''}
            disabled={isFieldDisabled('tenor')}
          />
          {errors.tenor && <FieldError>{errors.tenor}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 4: Effective Date, Maturity Date ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="effectiveDate">Effective Date</Label>
          <DateField
            id="effectiveDate"
            value={formData.effectiveDate ?? ''}
            disabled={isFieldDisabled('effectiveDate')}
            onChange={iso => handleInputChange('effectiveDate', iso)}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="maturityDate">Maturity Date</Label>
          <DateField
            id="maturityDate"
            value={formData.maturityDate ?? ''}
            disabled={isFieldDisabled('maturityDate')}
            onChange={iso => handleInputChange('maturityDate', iso)}
          />
        </FormGroup>

        <FormGroup />
      </FormRow>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/*  TWO-PANEL SWAP LEGS                                               */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      <SwapLegsContainer>
        {/* ── PAY LEG ── */}
        <SwapLegPanel variant="pay">
          <SwapLegHeader variant="pay">
            <SwapLegIcon variant="pay">↑</SwapLegIcon>
            <SwapLegTitle variant="pay">Pay Leg</SwapLegTitle>
          </SwapLegHeader>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="payCurrency">Currency*</Label>
              <Select
                id="payCurrency"
                value={formData.payCurrency}
                onChange={(e) => {
                  handleInputChange('payCurrency', e.target.value);
                  refreshSettleDate(setFormData, settleQuery(formData.tradeDate, e.target.value, formData.recvCurrency));
                }}
                disabled={isFieldDisabled('payCurrency')}
              >
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>{c.currencyCode}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="payNotional">Notional Amount*</Label>
              <Input
                id="payNotional"
                type="text"
                value={formData.payNotional}
                onChange={(e) => handleInputChange('payNotional', e.target.value)}
                onBlur={(e) => {
                  const parsed = parseAmountShorthand(e.target.value);
                  if (parsed > 0) {
                    setFormData((prev: any) => ({ ...prev, payNotional: formatAmountWithCommas(parsed) }));
                  }
                }}
                placeholder="e.g., 50MM"
                className={errors.payNotional ? 'error' : ''}
                disabled={isFieldDisabled('payNotional')}
              />
              {errors.payNotional && <FieldError>{errors.payNotional}</FieldError>}
            </FormGroup>
          </LegFormRow>

          <LegFormRowSingle>
            <FormGroup>
              <Label htmlFor="payRateType">Rate Type*</Label>
              <Select
                id="payRateType"
                value={formData.payRateType}
                onChange={(e) => handleInputChange('payRateType', e.target.value)}
                disabled={isFieldDisabled('payRateType')}
              >
                <option value="FIXED">Fixed</option>
                <option value="FLOAT">Floating</option>
              </Select>
            </FormGroup>
          </LegFormRowSingle>

          {formData.payRateType === 'FIXED' ? (
            <LegFormRowSingle>
              <FormGroup>
                <Label htmlFor="payFixedRate">Fixed Rate (%)*</Label>
                <Input
                  id="payFixedRate"
                  type="number"
                  step="0.0001"
                  value={formData.payFixedRate}
                  onChange={(e) => handleInputChange('payFixedRate', e.target.value)}
                  placeholder="e.g., 4.2500"
                  className={errors.payFixedRate ? 'error' : ''}
                  disabled={isFieldDisabled('payFixedRate')}
                />
                {errors.payFixedRate && <FieldError>{errors.payFixedRate}</FieldError>}
              </FormGroup>
            </LegFormRowSingle>
          ) : (
            <LegFormRow>
              <FormGroup>
                <Label htmlFor="payFloatIndex">Rate Index*</Label>
                <Select
                  id="payFloatIndex"
                  value={formData.payFloatIndex}
                  onChange={(e) => handleInputChange('payFloatIndex', e.target.value)}
                  className={errors.payFloatIndex ? 'error' : ''}
                  disabled={isFieldDisabled('payFloatIndex')}
                >
                  <option value="">Select</option>
                  <option value="SOFR">SOFR</option>
                  <option value="EURIBOR">EURIBOR</option>
                  <option value="SONIA">SONIA</option>
                  <option value="TONAR">TONAR</option>
                </Select>
                {errors.payFloatIndex && <FieldError>{errors.payFloatIndex}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="payFloatSpread">Spread (bps)</Label>
                <Input
                  id="payFloatSpread"
                  type="number"
                  step="0.01"
                  value={formData.payFloatSpread}
                  onChange={(e) => handleInputChange('payFloatSpread', e.target.value)}
                  placeholder="e.g., 25.00"
                  disabled={isFieldDisabled('payFloatSpread')}
                />
              </FormGroup>
            </LegFormRow>
          )}

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="payDayCount">Day Count</Label>
              <Select
                id="payDayCount"
                value={formData.payDayCount}
                onChange={(e) => handleInputChange('payDayCount', e.target.value)}
                disabled={isFieldDisabled('payDayCount')}
              >
                <option value="30/360">30/360</option>
                <option value="ACT/360">ACT/360</option>
                <option value="ACT/365">ACT/365</option>
                <option value="ACT/ACT">ACT/ACT</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="payFrequency">Payment Frequency</Label>
              <Select
                id="payFrequency"
                value={formData.payFrequency}
                onChange={(e) => handleInputChange('payFrequency', e.target.value)}
                disabled={isFieldDisabled('payFrequency')}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </Select>
            </FormGroup>
          </LegFormRow>
        </SwapLegPanel>

        {/* ── ARROW DIVIDER ── */}
        <SwapArrowDivider>
          <SwapArrow>⇄</SwapArrow>
        </SwapArrowDivider>

        {/* ── RECEIVE LEG ── */}
        <SwapLegPanel variant="receive">
          <SwapLegHeader variant="receive">
            <SwapLegIcon variant="receive">↓</SwapLegIcon>
            <SwapLegTitle variant="receive">Receive Leg</SwapLegTitle>
          </SwapLegHeader>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="recvCurrency">Currency*</Label>
              <Select
                id="recvCurrency"
                value={formData.recvCurrency}
                onChange={(e) => {
                  handleInputChange('recvCurrency', e.target.value);
                  refreshSettleDate(setFormData, settleQuery(formData.tradeDate, formData.payCurrency, e.target.value));
                }}
                className={errors.recvCurrency ? 'error' : ''}
                disabled={isFieldDisabled('recvCurrency')}
              >
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>{c.currencyCode}</option>
                ))}
              </Select>
              {errors.recvCurrency && <FieldError>{errors.recvCurrency}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="recvNotional">Notional Amount*</Label>
              <Input
                id="recvNotional"
                type="text"
                value={formData.recvNotional}
                onChange={(e) => handleInputChange('recvNotional', e.target.value)}
                onBlur={(e) => {
                  const parsed = parseAmountShorthand(e.target.value);
                  if (parsed > 0) {
                    setFormData((prev: any) => ({ ...prev, recvNotional: formatAmountWithCommas(parsed) }));
                  }
                }}
                placeholder="e.g., 45MM"
                className={errors.recvNotional ? 'error' : ''}
                disabled={isFieldDisabled('recvNotional')}
              />
              {errors.recvNotional && <FieldError>{errors.recvNotional}</FieldError>}
            </FormGroup>
          </LegFormRow>

          <LegFormRowSingle>
            <FormGroup>
              <Label htmlFor="recvRateType">Rate Type*</Label>
              <Select
                id="recvRateType"
                value={formData.recvRateType}
                onChange={(e) => handleInputChange('recvRateType', e.target.value)}
                disabled={isFieldDisabled('recvRateType')}
              >
                <option value="FIXED">Fixed</option>
                <option value="FLOAT">Floating</option>
              </Select>
            </FormGroup>
          </LegFormRowSingle>

          {formData.recvRateType === 'FIXED' ? (
            <LegFormRowSingle>
              <FormGroup>
                <Label htmlFor="recvFixedRate">Fixed Rate (%)*</Label>
                <Input
                  id="recvFixedRate"
                  type="number"
                  step="0.0001"
                  value={formData.recvFixedRate}
                  onChange={(e) => handleInputChange('recvFixedRate', e.target.value)}
                  placeholder="e.g., 3.5000"
                  className={errors.recvFixedRate ? 'error' : ''}
                  disabled={isFieldDisabled('recvFixedRate')}
                />
                {errors.recvFixedRate && <FieldError>{errors.recvFixedRate}</FieldError>}
              </FormGroup>
            </LegFormRowSingle>
          ) : (
            <LegFormRow>
              <FormGroup>
                <Label htmlFor="recvFloatIndex">Rate Index*</Label>
                <Select
                  id="recvFloatIndex"
                  value={formData.recvFloatIndex}
                  onChange={(e) => handleInputChange('recvFloatIndex', e.target.value)}
                  className={errors.recvFloatIndex ? 'error' : ''}
                  disabled={isFieldDisabled('recvFloatIndex')}
                >
                  <option value="">Select</option>
                  <option value="SOFR">SOFR</option>
                  <option value="EURIBOR">EURIBOR</option>
                  <option value="SONIA">SONIA</option>
                  <option value="TONAR">TONAR</option>
                </Select>
                {errors.recvFloatIndex && <FieldError>{errors.recvFloatIndex}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="recvFloatSpread">Spread (bps)</Label>
                <Input
                  id="recvFloatSpread"
                  type="number"
                  step="0.01"
                  value={formData.recvFloatSpread}
                  onChange={(e) => handleInputChange('recvFloatSpread', e.target.value)}
                  placeholder="e.g., 25.00"
                  disabled={isFieldDisabled('recvFloatSpread')}
                />
              </FormGroup>
            </LegFormRow>
          )}

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="recvDayCount">Day Count</Label>
              <Select
                id="recvDayCount"
                value={formData.recvDayCount}
                onChange={(e) => handleInputChange('recvDayCount', e.target.value)}
                disabled={isFieldDisabled('recvDayCount')}
              >
                <option value="30/360">30/360</option>
                <option value="ACT/360">ACT/360</option>
                <option value="ACT/365">ACT/365</option>
                <option value="ACT/ACT">ACT/ACT</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="recvFrequency">Payment Frequency</Label>
              <Select
                id="recvFrequency"
                value={formData.recvFrequency}
                onChange={(e) => handleInputChange('recvFrequency', e.target.value)}
                disabled={isFieldDisabled('recvFrequency')}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </Select>
            </FormGroup>
          </LegFormRow>
        </SwapLegPanel>
      </SwapLegsContainer>
    </>
  );
};

export default DerivativesCurrencySwapTicketScreen;
